'use client';

import Script from 'next/script';
import { usePathname, useSearchParams } from 'next/navigation';
import { useReportWebVitals } from 'next/web-vitals';
import { useEffect, useRef } from 'react';

import { handleDelegatedClick, makeScrollDepthTracker, trackEvent } from '@/lib/analytics';
import { TRACKING_SEARCH_PARAMS } from '@/lib/tracking-params';

type GoogleAnalyticsProps = {
  gaId?: string;
};

export default function GoogleAnalytics({ gaId }: GoogleAnalyticsProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  // The lazyOnload gtag config records the landing page_view itself
  // (send_page_view: true), so the navigation effect skips its initial run to
  // avoid double counting.
  const isFirstNavigation = useRef(true);
  const lastReportedPath = useRef<string | null>(null);

  // Core Web Vitals → GA4 (Core Web Vitals are an SEO ranking signal for content sites).
  useReportWebVitals((metric) => {
    trackEvent('web_vitals', {
      metric_name: metric.name,
      // CLS is a unitless ratio; scale it so GA's integer value stays meaningful.
      value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      metric_id: metric.id,
      metric_rating: metric.rating,
      // Without this the `page_path` dimension was 100% "(not set)" across 41778
      // web_vitals events: registered in GA4, never populated, so a bad LCP could
      // be seen site-wide but never traced to the page causing it. Read from
      // location rather than the `pathname` closure, which a client-side
      // navigation can leave stale before a late metric (CLS, INP) reports.
      page_path: typeof window === 'undefined' ? pathname : window.location.pathname || pathname,
      non_interaction: true,
    });
  });

  // page_view on every client-side navigation. The landing page_view is recorded
  // by the gtag config (send_page_view: true) so it survives lazyOnload; this
  // effect skips its first run and fires only on subsequent navigations.
  useEffect(() => {
    if (!gaId) return;

    const query = searchParams.toString();
    const pagePath = query ? `${pathname}?${query}` : pathname;

    if (isFirstNavigation.current) {
      isFirstNavigation.current = false;
      // Stripping the campaign params rewrites the URL, which re-runs this effect with a
      // changed search string. Record the path that cleanup will leave behind so the
      // rewrite is not mistaken for a navigation and counted a second time.
      const cleaned = new URLSearchParams(query);
      for (const key of TRACKING_SEARCH_PARAMS) cleaned.delete(key);
      const cleanedQuery = cleaned.toString();
      lastReportedPath.current = cleanedQuery ? `${pathname}?${cleanedQuery}` : pathname;
      return;
    }

    if (pagePath === lastReportedPath.current) return;
    if (typeof window.gtag !== 'function') return;
    lastReportedPath.current = pagePath;

    window.gtag('event', 'page_view', {
      page_path: pagePath,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [gaId, pathname, searchParams]);

  // Delegated click tracking (outbound / app-store / downloads) — registered once.
  useEffect(() => {
    if (!gaId) return;
    document.addEventListener('click', handleDelegatedClick, { capture: true });
    return () => document.removeEventListener('click', handleDelegatedClick, { capture: true });
  }, [gaId]);

  // Scroll-depth milestones, reset per page.
  useEffect(() => {
    if (!gaId) return;
    const tracker = makeScrollDepthTracker();
    window.addEventListener('scroll', tracker.onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', tracker.onScroll);
      tracker.reset();
    };
  }, [gaId, pathname]);

  if (!gaId) {
    return null;
  }

  return (
    <>
      <Script src={`https://www.googletagmanager.com/gtag/js?id=${gaId}`} strategy="lazyOnload" />
      {/*
        page_location is pinned explicitly so gtag.js reads the campaign from the config
        arguments rather than from window.location when it later processes the queue —
        without it, the cleanup below would race the landing page_view and strip the UTMs
        before they are ever recorded.
      */}
      {/*
        The bootstrap (gtag stub + config) runs afterInteractive while the heavy
        library above stays lazyOnload. With both on lazyOnload, window.gtag did
        not exist until well after hydration, so any event fired on mount — the
        404 tracker specifically — hit trackEvent's gtag-missing guard and was
        silently dropped: page_not_found recorded zero events in its first two
        days despite ~2500 sessions. The stub only queues into dataLayer, so the
        perf cost of running it early is nil, and config stays ahead of every
        event in the queue.
      */}
      <Script id="google-analytics" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          window.gtag = gtag;
          gtag('js', new Date());
          gtag('config', '${gaId}', {
            send_page_view: true,
            page_location: window.location.href,
            page_referrer: document.referrer
          });
          (function () {
            var url = new URL(window.location.href);
            var keys = ${JSON.stringify(TRACKING_SEARCH_PARAMS)};
            var changed = false;
            for (var i = 0; i < keys.length; i++) {
              if (url.searchParams.has(keys[i])) {
                url.searchParams.delete(keys[i]);
                changed = true;
              }
            }
            if (changed) {
              window.history.replaceState(null, '', url.pathname + url.search + url.hash);
            }
          })();
        `}
      </Script>
    </>
  );
}
